// 20章 ユニット及びその他工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_ACCESS_FLOOR = `
<svg viewBox="0 0 660 240" role="img" aria-label="フリーアクセスフロアの断面。コンクリートスラブの上に高さ調整できる支柱を立て、その上に正方形のパネルを敷き並べ、パネルの下の空間に電源・LAN・電話などの配線を通す。パネルの上にはタイルカーペット等の表面仕上材を張る。パネルには配線取出し口を設ける">
  <g fill="currentColor" opacity="0.25"><rect x="40" y="185" width="580" height="30"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="90" y="150" width="14" height="35"/>
    <rect x="230" y="150" width="14" height="35"/>
    <rect x="370" y="150" width="14" height="35"/>
    <rect x="510" y="150" width="14" height="35"/>
  </g>
  <g fill="currentColor" opacity="0.5"><rect x="60" y="130" width="540" height="20"/></g>
  <g fill="currentColor" opacity="0.75"><rect x="60" y="118" width="540" height="12"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="4 3">
    <path d="M130 172 q60 -14 120 0 q60 14 120 0 q60 -14 120 0"/>
  </g>
  <g fill="currentColor" opacity="0.85"><rect x="330" y="122" width="6" height="12"/></g>
  <g fill="currentColor" font-size="12.5">
    <text x="60" y="112">表面仕上材（タイルカーペット等）</text>
    <text x="336" y="118">配線取出し口</text>
    <text x="400" y="145">パネル（正方形）</text>
    <text x="130" y="200" opacity="0.85">床下に配線（電源・LAN・電話）</text>
    <text x="50" y="110" opacity="0"> </text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="45" font-weight="bold">フリーアクセスフロア（二重床）の断面（20.2.2）</text>
    <text x="40" y="70">支柱でパネルを持ち上げ、床下の空間に配線を自由に通す。事務室・電子計算機室の定番</text>
    <text x="118" y="145" opacity="0.85">支柱（高さ調整）</text>
  </g>
</svg>`;

const SVG_PARTITION = `
<svg viewBox="0 0 660 250" role="img" aria-label="可動間仕切と移動間仕切の違い。可動間仕切は上下レールを躯体にあと施工アンカーで固定し、分解・組立・移設して使える。移動間仕切は天井のハンガーレールからパネルを上吊りし、床にレールがなく、使わないときは端の格納庫にたたんで収納できる">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30" y="55" width="290" height="175" rx="8"/>
    <rect x="340" y="55" width="290" height="175" rx="8"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="175" y="80">可動間仕切</text>
    <text x="485" y="80">移動間仕切（上吊り式）</text>
  </g>
  <g fill="currentColor" opacity="0.7"><rect x="55" y="100" width="240" height="8"/><rect x="55" y="200" width="240" height="8"/></g>
  <g fill="currentColor" opacity="0.4"><rect x="95" y="108" width="55" height="92"/><rect x="160" y="108" width="55" height="92"/><rect x="225" y="108" width="55" height="92"/></g>
  <g fill="currentColor" opacity="0.7"><rect x="365" y="100" width="240" height="8"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.2"><path d="M400 108 v6 M465 108 v6 M530 108 v6"/></g>
  <g fill="currentColor" opacity="0.4"><rect x="378" y="114" width="45" height="100"/><rect x="443" y="114" width="45" height="100"/><rect x="508" y="114" width="45" height="100"/></g>
  <g fill="currentColor" font-size="11.5">
    <text x="50" y="124" opacity="0.9">上下レール</text>
    <text x="55" y="225" opacity="0.85">床・壁にあと施工アンカーで固定</text>
    <text x="360" y="124" opacity="0.9">天井のハンガーレールから上吊り</text>
    <text x="360" y="228" opacity="0.85">床にレールなし・端の格納庫へ収納</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8" text-anchor="middle">
    <text x="175" y="248">分解・組立・移設できる</text>
    <text x="485" y="248">パネルを移動して大空間に変えられる</text>
  </g>
</svg>`;

const SVG_TOILET_BOOTH = `
<svg viewBox="0 0 660 230" role="img" aria-label="トイレブースの構成。上部に笠木と頭つなぎで各パネルをつなぎ、化粧板のパネルとドアで個室を仕切る。脚部はステンレス製の幅木タイプで床から少し浮かせて掃除しやすくする。ドアはヒンジで開閉し、小口は防水処理する">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <line x1="60" y1="60" x2="600" y2="60"/>
    <line x1="90" y1="60" x2="90" y2="190"/>
    <line x1="260" y1="60" x2="260" y2="190"/>
    <line x1="430" y1="60" x2="430" y2="190"/>
    <line x1="600" y1="60" x2="600" y2="190"/>
  </g>
  <g fill="currentColor" opacity="0.15"><rect x="90" y="60" width="170" height="130"/><rect x="430" y="60" width="170" height="130"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.3"><rect x="262" y="62" width="166" height="126"/></g>
  <g fill="currentColor" opacity="0.85"><circle cx="415" cy="128" r="4"/></g>
  <g fill="currentColor" opacity="0.6"><rect x="90" y="190" width="18" height="14"/><rect x="242" y="190" width="18" height="14"/><rect x="412" y="190" width="18" height="14"/><rect x="582" y="190" width="18" height="14"/></g>
  <g fill="currentColor" font-size="12">
    <text x="60" y="52" font-weight="bold">トイレブースの構成（20.2.5）</text>
    <text x="310" y="128" text-anchor="middle">ドア</text>
    <text x="422" y="124">ヒンジ</text>
    <text x="110" y="200">脚部（ステンレス・幅木タイプ）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="380" y="52">笠木・頭つなぎで上部を連結</text>
    <text x="60" y="222">パネルはメラミン／ポリエステル化粧板（F☆☆☆☆）。小口は防水処理、ねじはステンレス製</text>
  </g>
</svg>`;

const SVG_STAIR_NOSING = `
<svg viewBox="0 0 660 210" role="img" aria-label="階段滑り止めの取付け。接着工法は下地を乾燥・清掃してからエポキシ樹脂系接着剤と小ねじで段鼻に取り付ける。埋込み工法はコンクリートにアンカーを埋め込み、両端を押さえて間隔300mm程度で堅固に取り付ける">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M40 150 h120 v-45 h120"/>
    <path d="M360 150 h120 v-45 h120"/>
  </g>
  <g fill="currentColor" opacity="0.85"><path d="M155 105 h10 v-8 h-14 z"/><path d="M475 105 h10 v-8 h-14 z"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.2"><path d="M470 105 v14 M482 105 v14"/></g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="160" y="55">接着工法（標準）</text>
    <text x="480" y="55">埋込み工法</text>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="40" y="80">・下地を乾燥・清掃</text>
    <text x="40" y="100">・エポキシ樹脂系接着剤</text>
    <text x="40" y="180" opacity="0.85">・5℃以下は施工しない</text>
    <text x="360" y="80">・アンカーを埋め込む</text>
    <text x="360" y="100">・両端を押さえる</text>
    <text x="360" y="180" opacity="0.85">・間隔300mm程度で堅固に</text>
  </g>
</svg>`;

const SVG_CURTAIN_PLEAT = `
<svg viewBox="0 0 660 220" role="img" aria-label="カーテンのひだの種類と、取付け幅に対する生地の倍数。つまみひだ（三つ山・二つ山）は2.0倍以上、箱ひだ・片ひだは1.5倍以上、プレーンひだは1.1倍以上。倍数が大きいほど生地をたっぷり使い、ひだが深く豪華に見える">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M40 70 q8 -18 16 0 q8 -18 16 0 q8 -18 16 0 l0 90 q-24 8 -48 0 z"/>
    <path d="M250 70 q22 -20 44 0 l0 90 q-22 8 -44 0 z"/>
    <path d="M460 72 q40 -6 80 0 l0 88 q-40 8 -80 0 z"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="65" y="190">つまみひだ</text>
    <text x="272" y="190">箱ひだ・片ひだ</text>
    <text x="500" y="190">プレーンひだ</text>
  </g>
  <g fill="currentColor" font-size="15" font-weight="bold" text-anchor="middle">
    <text x="65" y="212">2.0倍以上</text>
    <text x="272" y="212">1.5倍以上</text>
    <text x="500" y="212">1.1倍以上</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="40" font-weight="bold">カーテンのひだの倍数（表20.2.1・20.2.16）</text>
    <text x="40" y="58">倍数＝取付け幅に対して使う生地の幅。大きいほどひだが深く豪華。ひだ間隔は120mm程度</text>
  </g>
</svg>`;

const SVG_STONE_WALL = `
<svg viewBox="0 0 660 250" role="img" aria-label="間知石積み。布積みは横目地が通る積み方、谷積みは石を斜めに互い違いに積む方式で標準はこちら。断面では、石の裏に裏込めコンクリートを打ちながら積み上げ、土圧を支える。水抜きは硬質塩ビ管を千鳥に設け、元部に土砂流出防止マットと砂利を置いて水を逃がす">
  <g fill="none" stroke="currentColor" stroke-width="1.4">
    <rect x="30" y="55" width="230" height="150"/>
    <path d="M30 88 h230 M30 121 h230 M30 154 h230 M30 187 h230 M75 55 v33 M120 88 v33 M165 55 v33 M210 88 v33 M75 121 v33 M120 154 v33 M165 121 v33 M210 154 v33 M97 187 v18 M188 187 v18"/>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="1.4">
    <rect x="410" y="55" width="220" height="150"/>
    <path d="M410 90 l70 -35 M480 55 l70 35 M550 55 l70 35 M410 90 l70 35 M480 125 l70 -35 M480 125 l70 35 M550 90 l70 35 M410 160 l70 -35 M480 195 l70 -35 M550 160 l70 35 M480 195 l70 10"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="145" y="45">布積み</text>
    <text x="520" y="45">谷積み（標準）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="30" y="228">横目地が水平に通る</text>
    <text x="410" y="228">石を斜めに互い違いに積む（かみ合いがよい）</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.8" text-anchor="middle">
    <text x="330" y="248">裏込めコンクリートを打ちながら積み、水抜き管（径50mm以上）を千鳥に設ける（20.4.3）</text>
  </g>
</svg>`;

const ch20: Record<string, Commentary> = {
  "20.1.1": {
    plainSummary:
      "この章は、現場で取り付けるユニット製品類・プレキャストコンクリート・間知石／コンクリート間知ブロック積みに適用する。1章とセットで適用する。",
    why: "「ユニット工事」は、工場で作った製品を現場で取り付ける工事の総称です。フリーアクセスフロア・間仕切・トイレブース・手すり・鏡・ブラインド・カーテンなど、内装を仕上げる最後の建築付属品が幅広く含まれます。",
  },

  "20.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②所定の位置・取付け方法で設置され所要の仕上り状態 ③使用性・耐久性に有害な欠陥がないこと。",
    why: "ユニット製品は「取付け」が品質の決め手。製品自体は工場品質でも、現場の固定が甘いとぐらつき・脱落につながります。特にアンカーによる堅固な固定が繰り返し求められます。",
  },

  "20.2.1": {
    plainSummary: "この節は、現場で取り付けるユニット製品類の工事に適用する。",
  },

  "20.2.2": {
    plainSummary:
      "事務室・電子計算機室等に用いるフリーアクセスフロア（二重床）。寸法・高さ・耐震性能・所定荷重・帯電防止性能等は特記。試験はJIS A 1450により、耐荷重・耐衝撃・ローリングロード・耐燃焼の各性能が規定（例：静荷重で変形5.0mm以下・残留3.0mm以下）。パネルは配線取出し機能を持ち増設可、鋼製は防錆処理。工法は製造所仕様による。",
    why: "フリーアクセスフロア（OAフロア）は、床を支柱で持ち上げて「二重床」にし、床下の空間に電源・LAN・電話の配線を自由に通す床です。オフィスや電算室では配線変更が頻繁なので、床をめくって配線し直せるこの床が標準になります。人と機器が乗るので耐荷重・耐衝撃の性能試験が重要です。",
    points: [
      "床下空間に配線を通す二重床（配線変更が容易）",
      "JIS A 1450で耐荷重・耐衝撃等を試験",
      "パネルは配線取出し口を持ち、増設できる",
    ],
    glossary: [
      {
        term: "フリーアクセスフロア",
        meaning:
          "支柱でパネルを持ち上げた二重床。床下に配線を通し、パネルをめくって配線変更できる。OAフロアとも呼ぶ。",
      },
      {
        term: "帯電防止性能",
        meaning:
          "静電気をためない性能。電算室では静電気が機器の誤作動・故障を招くため求められる。",
      },
    ],
    diagrams: [
      {
        svg: SVG_ACCESS_FLOOR,
        caption: "フリーアクセスフロア（二重床）の断面（20.2.2）",
      },
    ],
  },

  "20.2.3": {
    plainSummary:
      "非耐力壁の間仕切として建物内部に取り付け、分解・組立・移設して使える標準的な可動間仕切。JIS A 6512により種類・遮音性は特記（放散量規定材はF☆☆☆☆）。電灯スイッチ・コンセント・配線を隠ぺいでき、電気設備の技術基準・内線規程に適合。上下レールはあと施工アンカーで堅固に固定、取合い部にパッキン材、天井固定時は荷重・層間変位に耐えるようにする。",
    why: "可動間仕切は「間取りを後から変えられる壁」です。オフィスのレイアウト変更に合わせて分解・移設できます。上下レールを躯体にアンカーで固定するのが基本で、パネル内に配線を隠せるのが便利な点。層間変位（地震時に上下階がずれる量）に耐える配慮が求められます。",
    points: [
      "分解・組立・移設できる非耐力壁の間仕切",
      "上下レールをあと施工アンカーで堅固に固定",
      "配線・スイッチを隠ぺいできる",
    ],
    diagrams: [
      {
        svg: SVG_PARTITION,
        caption: "可動間仕切と移動間仕切の違い（20.2.3・20.2.4）",
      },
    ],
  },

  "20.2.4": {
    plainSummary:
      "移動・格納できる標準的な上吊りパネル式間仕切。操作方法・表面材・遮音性・ハンガーレール等は特記。下地・レール・ランナー・取付け部品は、いずれも所定荷重（取付け全重量やパネル重量の5倍等）に耐えること。ハンガーレールは下地補強材に溶接、または躯体にあと施工アンカーで堅固に取り付ける。",
    why: "移動間仕切は「天井から吊るして動かせる大きな引き戸のような壁」です。宴会場や多目的室で、パネルを引き出して部屋を仕切ったり、格納庫にたたんで大空間に戻したりします。床にレールがないので通行を妨げません。全体を天井で吊るため、レールや金物には重量の5倍という大きな安全率が求められます。",
    points: [
      "天井のハンガーレールから上吊りする可動式の壁",
      "床にレールがない（上吊り式）",
      "各部材は所定荷重（重量の5倍等）に耐えること",
    ],
    glossary: [
      {
        term: "ランナー",
        meaning:
          "ハンガーレールに沿って走る車輪状の部品。この上にパネルを吊り下げて移動させる。",
      },
    ],
  },

  "20.2.5": {
    plainSummary:
      "屋内で使うトイレブース。表面材はメラミン樹脂系またはポリエステル樹脂系化粧板、主要基材はJIS A 6512（放散量規定材はF☆☆☆☆）。笠木・脚部・見切り金物・頭つなぎは耐食性のある金物で、脚部はステンレス製（特記なければ幅木タイプ）。開閉耐久性はJIS A 4702の繰返し試験に合格。小口は防水処理、頭つなぎ等の小ねじはステンレス製。",
    why: "トイレブースは水掛かり・湿気の多い場所なので、防水と防錆が要です。脚部を床から浮かせる「幅木タイプ」にするのは、掃除の水が金物を傷めないようにし、清掃しやすくするため。ステンレスの金物と小口の防水処理でサビと劣化を防ぎます。",
    points: [
      "化粧板パネルで個室を仕切る（F☆☆☆☆）",
      "脚部はステンレス製・幅木タイプ（防錆・清掃性）",
      "小口は防水処理、ねじはステンレス製",
    ],
    diagrams: [
      {
        svg: SVG_TOILET_BOOTH,
        caption: "トイレブースの構成（20.2.5）",
      },
    ],
  },

  "20.2.6": {
    plainSummary:
      "手すりの材料の種類・仕上げは特記による。塗装は18章［塗装工事］による。",
  },

  "20.2.7": {
    plainSummary:
      "階段滑り止め（ノンスリップ）。材種・形状・寸法は特記。取付けは特記がなければ接着工法（下地乾燥・清掃後、エポキシ樹脂系接着剤＋小ねじ。5℃以下は施工しない）。埋込み工法はアンカーで両端を押さえ、間隔300mm程度で堅固に取り付ける。",
    why: "階段の段鼻（先端）に付ける滑り止めです。接着工法が標準で、接着剤に加えて小ねじも併用するのは、人が繰り返し踏むため接着だけでは剥がれやすいから。気温5℃以下で施工しないのは接着剤が硬化不良を起こすためです。",
    diagrams: [
      {
        svg: SVG_STAIR_NOSING,
        caption: "階段滑り止めの取付け工法（20.2.7）",
      },
    ],
  },

  "20.2.8": {
    plainSummary:
      "床目地棒はステンレス製、厚さ5〜6mm・高さ12mmを標準とし、アンカーは間隔500mm程度に取り付ける。",
    glossary: [
      {
        term: "床目地棒",
        meaning:
          "床の仕上げの見切りや伸縮目地に埋め込む金属の棒。異なる床材の境目をきれいに納める。",
      },
    ],
  },

  "20.2.9": {
    plainSummary:
      "黒板は特記による（特記なければJIS S 6007、焼付けの鋼製黒板またはほうろう黒板、アルミ枠・チョーク溝・チョーク入れ・粉入れ付き）。ホワイトボードは特記による。",
  },

  "20.2.10": {
    plainSummary:
      "鏡は縁なしで防湿性を有するもの。ガラスはJIS R 3220、厚さは特記（なければ5mm）。取付けはゴム座等を当て、ステンレス製等の適切な金物で行う。",
    why: "洗面所等の鏡は湿気で裏面の反射膜が劣化（シケ・黒ずみ）しやすいため、防湿性のある鏡を使います。ゴム座を挟むのは、ガラスが金物や壁に直接当たって割れるのを防ぐクッションです。",
  },

  "20.2.11": {
    plainSummary:
      "表示関係。①ガラススクリーンへの対人衝突防止表示（形状・寸法・材質は特記）②非常用進入口等の法令表示は消防法適合の市販品 ③室名札・ピクトグラム・案内板等の形状・材質・書体・取付け形式等は特記。",
    glossary: [
      {
        term: "ピクトグラム",
        meaning:
          "トイレ・非常口などを絵記号で示す案内表示。言語によらず誰にでも伝わる。",
      },
    ],
  },

  "20.2.12": {
    plainSummary:
      "タラップの材料の種類・仕上げは特記（なければステンレス製で研磨等の仕上げは不要）。塗装は18章による。",
    glossary: [
      {
        term: "タラップ",
        meaning:
          "屋上・ピット・煙突等へ昇り降りするために壁面に固定した金属製のはしご。",
      },
    ],
  },

  "20.2.13": {
    plainSummary:
      "煙突ライニング。成形ライニング材は実績等の資料を監督職員に提出（適用安全使用温度は特記）。キャスタブル耐火材は製造所指定品。工法は特記による。",
    glossary: [
      {
        term: "煙突ライニング",
        meaning:
          "煙突の内側に張る耐熱・耐酸の内張り。高温・腐食性の排ガスから躯体を守る。",
      },
    ],
  },

  "20.2.14": {
    plainSummary:
      "ブラインド。横形／縦形は特記。横形はJIS A 4801（なければギヤ式・スラット幅25mm・アルミ合金製スラット・鋼製ボックス／レール）。縦形は操作方法（なければ2本操作コード方式）、スラットは焼付け塗装アルミまたは防炎表示のクロススラット。取付け幅・高さは現場実測。横形のブラケットは幅1.8m未満は両端、1.8m以上は中間に1個以上増やして小ねじで堅固に固定。",
    why: "ブラインドは羽根（スラット）の角度で採光と視線を調整する日よけです。幅が広いと自重でたわむため、1.8m以上では中間にブラケットを追加してたわみ・脱落を防ぎます。製作寸法を現場実測で決めるのは、窓枠の実寸に合わせないと隙間や干渉が出るためです。",
    points: [
      "横形／縦形があり、羽根の角度で採光・視線を調整",
      "取付け寸法は現場実測で決める",
      "幅1.8m以上は中間ブラケットを追加",
    ],
  },

  "20.2.15": {
    plainSummary:
      "ロールスクリーン。操作方式・幅・高さは特記。スクリーンは消防法の防炎表示があるもの（材種・品質は特記）。色見本を監督職員に提出。巻取りパイプ・ウェイトバー・操作コード等は特記（なければ製造所仕様）。取付け寸法は現場実測で定める。",
    glossary: [
      {
        term: "ロールスクリーン",
        meaning:
          "1枚の生地を巻取りパイプに巻き上げて開閉する日よけ・間仕切。すっきりした見た目で窓・出入口に使う。",
      },
    ],
  },

  "20.2.16": {
    plainSummary:
      "カーテン及びカーテンレール。形式（シングル／ダブル・片引き／引分け）・操作方式は特記。生地は防炎表示があるもので色見本を提出。レールはJIS A 4802（なければ強さ区分10-90・アルミ押出し材・アルマイト・角形）。ひだは表20.2.1（つまみひだ2.0倍以上・箱ひだ／片ひだ1.5倍以上・プレーンひだ1.1倍以上）、間隔120mm程度。取付け寸法は現場実測。下端は腰窓で窓下200mm程度下げ、掃出し窓は床に触れない程度。",
    why: "カーテンの「ひだ」の倍数は、仕上がり幅に対してどれだけ生地をたっぷり使うかを表します。つまみひだ2.0倍以上は生地を2倍以上使うので、ひだが深く豪華に見えます。プレーンひだ1.1倍はほぼ平らで安価。生地は消防法の防炎表示が必須です。下端を窓下200mm下げるのは、光漏れとカーテンのめくれを抑えるためです。",
    points: [
      "ひだの倍数：つまみ2.0／箱・片1.5／プレーン1.1倍以上",
      "生地は消防法の防炎表示が必須",
      "取付け寸法は現場実測、下端は腰窓で窓下200mm下げる",
    ],
    glossary: [
      {
        term: "ひだ（プリーツ）",
        meaning:
          "カーテン上部のたたみ。倍数が大きいほど生地を多く使い、ひだが深く立体的になる。",
      },
      {
        term: "インターロック（幅継ぎ）",
        meaning:
          "生地の幅が足りないとき、布端をほつれ止めしながら縫い継ぐ加工。",
      },
    ],
    diagrams: [
      {
        svg: SVG_CURTAIN_PLEAT,
        caption: "カーテンのひだの種類と倍数（表20.2.1）",
      },
    ],
  },

  "20.3.1": {
    plainSummary:
      "この節は、手すり・段板・ルーバー等の簡易なプレキャストコンクリート（PCa）の工場製品の工事に適用する。",
    glossary: [
      {
        term: "プレキャストコンクリート（PCa）",
        meaning:
          "工場であらかじめ成形・養生したコンクリート製品。現場で組み立てる。品質が安定し工期短縮になる。",
      },
    ],
  },

  "20.3.2": {
    plainSummary:
      "材料。コンクリートは表6.2.1のⅡ類に準じ材料は6章3節による。鉄筋は5章2節による。補強鉄線はJIS G 3532またはJIS G 3551（径・網目は特記）。取付け金物は防錆処理（コンクリート埋込み部を除く）。",
  },

  "20.3.3": {
    plainSummary:
      "製作。設計基準強度は特記（なければ水セメント比55%以下・単位セメント量300kg/㎥以上を満たす調合強度）。スランプは12cm。見え掛り部の型枠は良好な仕上りが得られるもの、せき板は平滑で傷・穴等のないもの。鉄筋は所定形状に配筋し交差部を緊結（必要に応じ溶接）、かぶり厚さは5.3.5による。取付け金物は打込みが原則（承諾を受けてあと付け可）。所定強度まで必要に応じ蒸気養生、汚れ・ねじれ・ひび割れ・破損なく貯蔵。",
    why: "工場製品は見え掛り（仕上げ面）の美しさが商品価値。型枠（せき板）を平滑にし、傷・目違いのない面を作ります。蒸気養生は、加熱して短時間で強度を出し、製品の回転（型枠の再利用）を早める工場ならではの方法です。",
    points: [
      "見え掛り面の型枠・せき板は平滑・無傷に",
      "取付け金物は打込みが原則",
      "必要に応じ蒸気養生で早期に強度を出す",
    ],
  },

  "20.3.4": {
    plainSummary: "プレキャストコンクリート製品の取付け方法は特記による。",
  },

  "20.4.1": {
    plainSummary:
      "この節は、高さ2m以下の擁壁等、土圧等の小さい場合に使う間知石及びコンクリート間知ブロック積みに適用する。",
    glossary: [
      {
        term: "間知石（けんちいし）積み",
        meaning:
          "四角錐状に加工した石を斜面に積んで造る石積みの擁壁。裏にコンクリートを詰める「練積み」で土を留める。",
      },
    ],
  },

  "20.4.2": {
    plainSummary:
      "材料。間知石は控えを四方落とし、控え長さは面の最小辺の1.2倍以上（材種は特記）、表面はほぼ平らで合端は30mm程度。コンクリート間知ブロックはJIS A 5371の積みブロック（種類・質量区分は特記）。地業材料・コンクリート・目地モルタル（容積比セメント1：砂2）・水抜き管（表21.2.1のVP）が定められる。",
    glossary: [
      {
        term: "控え（ひかえ）",
        meaning:
          "間知石の奥行方向の長さ。長いほど土中に深く食い込み、擁壁が安定する。面の最小辺の1.2倍以上とする。",
      },
      {
        term: "合端（あいば）",
        meaning: "隣り合う石が接する面のこと。ここをそろえて据えるときれいに積める。",
      },
    ],
  },

  "20.4.3": {
    plainSummary:
      "工法。土工事は3章、地業は砂利地業による。間知石積みは練積みとし、布積みまたは谷積み（特記なければ谷積み）。根石から積み始め、控えが法面に直角になるよう据え、裏込めコンクリートを打ち込みながら積み上げる。1日の積上げは1.2m以下。伸縮調整目地は15m程度ごと等に設置。水抜きは径50mm以上の塩ビ管を2〜3㎡に1個の割合で千鳥に設け、元部に土砂流出防止マット（200×200）と砂利を置く。ブロック積みは合端合せに鉄棒を用い（げんのうは使わない）、半ブロックで端部を処理。コンクリート温度2℃を下回るおそれ・降雨降雪時は適切な養生（できなければ打込まない）。",
    why: "間知石積みの核心は「裏込めコンクリート」と「水抜き」です。石を積みながら裏にコンクリートを打つ練積みで、土圧をしっかり支えます。そして擁壁の裏にたまった水を抜かないと、水圧で崩れるため、水抜き管を千鳥（互い違い）に配置して排水します。標準の谷積みは石を斜めに互い違いに積むので、かみ合いがよく安定します。",
    points: [
      "練積み＝裏込めコンクリートを打ちながら積む",
      "標準は谷積み（斜めに互い違い、かみ合いがよい）",
      "水抜き管を千鳥に設け背面の水圧を逃がす",
      "1日の積上げは1.2m以下",
    ],
    diagrams: [
      {
        svg: SVG_STONE_WALL,
        caption: "間知石積み（布積み／谷積み）と水抜き（20.4.3）",
      },
    ],
  },

  "20.4.4": {
    plainSummary:
      "養生。コンクリート温度が2℃を下回るおそれ・降雨降雪時は22.5.5により養生。必要に応じシート等で覆い適宜散水。硬化するまで振動・衝撃を与えない。",
    why: "打ちたてのコンクリートは低温で凍害を受け、雨で表面が流され、振動で組織が乱れます。硬化するまで温度・水分・振動から守るのが養生の目的です。",
  },
};

export default ch20;
